import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Calendar, Clock, MapPin, Handshake, CalendarDays, RefreshCw, BriefcaseBusiness, ClipboardList, Mail, Target, Leaf, Home, Brain, Ticket, Coffee, Mic, CheckCircle2 } from 'lucide-react';
import BrandStripes from '../../components/common/BrandStripes';
import BorderGlow from '../../components/common/BorderGlow';
import HexagonCornerDecoration from '../../components/common/HexagonCornerDecoration';
import { useTheme } from '../../context/ThemeContext';
import { useAuth } from '../../context/AuthContext';
import { fetchEventosPublicos, inscribirseEvento, Evento as ApiEvento } from '../../services/eventoService';
import Logo from '../../assets/images/logo.png'

const colors = {
    primary: '#61257B',
    primaryDark: '#420f54',
    primaryLight: '#C1348B',
    secondary: '#C1348B',
    secondaryLight: '#FFD97C',
    accent: '#FFD97C',
    accentDark: '#EA8406',
    moradoPrincipal: '#61257B',
    moradoOscuro: '#420f54',
    moradoVariante: '#C1348B',
};

function useInView(threshold = 0.1): [React.RefObject<HTMLElement>, boolean] {
    const ref = useRef<HTMLElement>(null);
    const [inView, setInView] = useState(false);
    useEffect(() => {
        if (ref.current) {
            const rect = ref.current.getBoundingClientRect();
            if (rect.top < window.innerHeight && rect.bottom > 0) { setInView(true); return; }
        }
        const obs = new IntersectionObserver(
            ([entry]) => { if (entry.isIntersecting) setInView(true); },
            { threshold }
        );
        if (ref.current) obs.observe(ref.current);
        return () => obs.disconnect();
    }, [threshold]);
    return [ref, inView];
}

// ── Tipos ──
interface NetworkingEvent {
    id: string;
    rawId?: number;
    title: string;
    date: string;
    time: string;
    frequency: string;
    format: string;
    capacity: number;
    registered: number;
    icon: React.ReactNode;
    color: string;
    gradient: string;
    description: string;
    perks: string[];
    tag: string;
    image?: string;
    isRegistered?: boolean;
}

const DEFAULT_NETWORKING_IMAGES = [
    'https://images.unsplash.com/photo-1511795409834-ef04bbd61622?auto=format&fit=crop&w=800&q=80', // Founders lounge networking
    'https://images.unsplash.com/photo-1556761175-5973dc0f32e7?auto=format&fit=crop&w=800&q=80', // Investor breakfast meeting
    'https://images.unsplash.com/photo-1497215728101-856f4ea42174?auto=format&fit=crop&w=800&q=80', // Open house cowork
    'https://images.unsplash.com/photo-1524178232363-1fb2b075b655?auto=format&fit=crop&w=800&q=80', // Masterclass presentation
];

const mapApiEventoToNetworking = (api: ApiEvento): NetworkingEvent => {
    const color = api.color || colors.primary;
    const gradient = api.gradiente || `linear-gradient(145deg, ${api.color_dark || '#420f54'}, ${color})`;
    const tag = api.tags && api.tags.length > 0 ? api.tags[0] : (api.estado === 'en curso' ? 'En curso' : 'Próximo');
    const eventImg = api.imagen || DEFAULT_NETWORKING_IMAGES[(api.id || 0) % DEFAULT_NETWORKING_IMAGES.length];

    return {
        id: `ne-${api.id}`,
        rawId: api.id,
        title: api.titulo,
        date: api.fecha || 'Próximamente',
        time: api.hora || 'Por definir',
        frequency: api.frecuencia || 'Mensual',
        format: api.formato || 'Presencial',
        capacity: api.capacidad || 50,
        registered: api.total_inscritos || 0,
        icon: api.icono || <Mic size={32} />,
        color,
        gradient,
        description: api.descripcion || '',
        perks: Array.isArray(api.perks) && api.perks.length > 0 ? api.perks : ['Acceso directo', 'Networking'],
        tag,
        image: eventImg,
        isRegistered: api.is_registered,
    };
};

interface Testimonial {
    id: string;
    name: string;
    role: string;
    company: string;
    quote: string;
    avatar: string;
    initials: string;
    color: string;
    connection: string;
}

interface NetworkingStat {
    value: string;
    label: string;
    sublabel: string;
    icon: React.ReactNode;
    color: string;
}

interface HowItWorksStep {
    step: string;
    title: string;
    description: string;
    icon: React.ReactNode;
    color: string;
}

// ── Datos ──
const initialEvents: NetworkingEvent[] = [
    {
        id: 'ne1',
        title: 'Noche de Founders',
        date: '28 Mar 2026',
        time: '7:00 PM',
        frequency: 'Mensual',
        format: 'Presencial',
        capacity: 60,
        registered: 47,
        icon: <Mic size={32} />,
        color: colors.primary,
        gradient: `linear-gradient(145deg, #420f54, #61257B)`,
        description: 'El espacio más íntimo y honesto de Co Marca. 6 founders presentan sus proyectos en 5 minutos ante una sala de pares, mentores e inversores. Sin jueces, sin competencia — solo conversaciones que mueven el ecosistema.',
        perks: ['6 pitches en vivo', 'Q&A abierto', 'Coctel de cierre'],
        tag: 'Próximo',
        image: 'https://images.unsplash.com/photo-1511795409834-ef04bbd61622?auto=format&fit=crop&w=800&q=80',
    },
    {
        id: 'ne2',
        title: 'Desayuno con Inversores',
        date: '10 Abr 2026',
        time: '8:30 AM',
        frequency: 'Trimestral',
        format: 'Presencial · Solo invitación',
        capacity: 20,
        registered: 18,
        icon: <Coffee size={32} />,
        color: colors.accentDark,
        gradient: `linear-gradient(145deg, #7a4800, ${colors.accentDark})`,
        description: 'Una mesa redonda exclusiva donde 4-5 inversores ángeles activos de Bolivia se sientan con founders seleccionados para conversaciones directas sobre rondas, valuaciones y estrategia. El formato más exclusivo de Co Marca.',
        perks: ['Acceso solo por selección', '5 inversores activos', 'Follow-up garantizado'],
        tag: 'Exclusivo · 2 cupos',
        image: 'https://images.unsplash.com/photo-1556761175-5973dc0f32e7?auto=format&fit=crop&w=800&q=80',
    },
    {
        id: 'ne3',
        title: 'Co Marca Open House',
        date: 'Todos los Miércoles',
        time: '6:00 PM',
        frequency: 'Semanal',
        format: 'Presencial · Entrada libre',
        capacity: 80,
        registered: 34,
        icon: <Home size={32} />,
        color: colors.secondary,
        gradient: `linear-gradient(145deg, #61257B, ${colors.secondary})`,
        description: 'Cada miércoles Co Marca abre sus puertas al público. Ven a conocer el espacio, conectar con miembros activos, y descubrir por qué somos el hub de referencia en Bolivia. Sin registro previo, sin agenda — solo conexiones naturales.',
        perks: ['Sin registro previo', 'Tour del espacio', 'Membresía de prueba gratis'],
        tag: 'Semanal · Hoy',
        image: 'https://images.unsplash.com/photo-1497215728101-856f4ea42174?auto=format&fit=crop&w=800&q=80',
    },
    {
        id: 'ne4',
        title: 'Masterclass + Networking',
        date: '24 Abr 2026',
        time: '6:30 PM',
        frequency: 'Mensual',
        format: 'Presencial + Streaming',
        capacity: 100,
        registered: 61,
        icon: <Brain size={32} />,
        color: colors.accentDark,
        gradient: `linear-gradient(145deg, #451759, ${colors.accentDark})`,
        description: '60 minutos de masterclass con un referente del ecosistema boliviano o latinoamericano, seguidos de 45 minutos de networking guiado con dinámicas que garantizan que salgas con al menos 3 contactos clave.',
        perks: ['Speaker internacional', 'Networking guiado', 'Grabación disponible'],
        tag: 'Híbrido',
        image: 'https://images.unsplash.com/photo-1524178232363-1fb2b075b655?auto=format&fit=crop&w=800&q=80',
    },
];

const testimonials: Testimonial[] = [
    {
        id: 'tm1', name: 'Renata Solíz', role: 'CEO', company: 'Nativa Foods',
        quote: 'En la Noche de Founders conocí a mi cofundador técnico. Tres meses después levantamos nuestra primera ronda. Co Marca no es solo cowork — es donde ocurren las coincidencias que cambian negocios.',
        avatar: 'https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?auto=format&fit=crop&w=200&q=80', initials: 'RS',
        color: colors.primary, connection: 'Encontró co-fundador aquí',
    },
    {
        id: 'tm2', name: 'Javier Poma', role: 'Inversor Ángel', company: 'Fondo Impulso Bolivia',
        quote: 'El Desayuno con Inversores de Co Marca tiene el mejor deal flow de La Paz. La calidad de los founders que llegan aquí es consistentemente alta. Hice 2 inversiones directas en el último año.',
        avatar: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?auto=format&fit=crop&w=200&q=80', initials: 'JP',
        color: colors.accentDark, connection: '2 inversiones realizadas',
    },
    {
        id: 'tm3', name: 'Sofía Antezana', role: 'Directora', company: 'Startup Bolivia',
        quote: 'El Open House de los miércoles se convirtió en parte de mi rutina. Cada semana salgo con una conversación que no esperaba y que siempre termina siendo útil. Es el mejor networking orgánico de la ciudad.',
        avatar: 'https://images.unsplash.com/photo-1580489944761-15a19d654956?auto=format&fit=crop&w=200&q=80', initials: 'SA',
        color: colors.secondary, connection: 'Asiste cada semana',
    },
    {
        id: 'tm4', name: 'Diego Balcázar', role: 'Fundador', company: 'TechLab Bolivia',
        quote: 'Gracias a la Masterclass de marzo cerré una alianza estratégica con una empresa de Santiago. El networking guiado de Co Marca funciona — no es esa dinámica incómoda de intercambiar tarjetas sin contexto.',
        avatar: 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?auto=format&fit=crop&w=200&q=80', initials: 'DB',
        color: colors.accentDark, connection: 'Alianza internacional',
    },
];

const stats: NetworkingStat[] = [
    { value: '2,400+', label: 'Conexiones generadas', sublabel: 'en el último año', icon: <Handshake size={32} />, color: colors.primary },
    { value: '18', label: 'Eventos de networking', sublabel: 'cada trimestre', icon: <CalendarDays size={32} />, color: colors.secondary },
    { value: '92%', label: 'Vuelven al siguiente', sublabel: 'tasa de retorno', icon: <RefreshCw size={32} />, color: colors.secondaryLight },
    { value: 'USD 320K', label: 'En negocios cerrados', sublabel: 'entre miembros 2025', icon: <BriefcaseBusiness size={32} />, color: colors.accentDark },
];

const steps: HowItWorksStep[] = [
    { step: '01', title: 'Elige tu evento', description: 'Explora la agenda y selecciona el formato que más te encaje — desde el Open House libre hasta el Desayuno con Inversores por invitación.', icon: <ClipboardList size={32} />, color: colors.primary },
    { step: '02', title: 'Regístrate o llega directo', description: 'Algunos eventos requieren inscripción previa, otros son de entrada libre. Siempre te decimos qué esperar y con quién estarás.', icon: <Mail size={32} />, color: colors.secondary },
    { step: '03', title: 'Conecta con intención', description: 'Nuestros eventos tienen dinámicas diseñadas para que las conversaciones sean sustanciales, no superficiales. Llegas con propósito, sales con contactos reales.', icon: <Target size={32} />, color: colors.secondaryLight },
    { step: '04', title: 'Mantén el vínculo', description: 'La comunidad Co Marca no termina al salir del evento. Grupos, canales y seguimiento activo garantizan que las conexiones sobrevivan al café de bienvenida.', icon: <Leaf size={32} />, color: colors.accentDark },
];

// ── Event Card ──
function EventCard({
    event,
    index,
    onRegister,
}: {
    event: NetworkingEvent;
    index: number;
    onRegister: (e: NetworkingEvent) => void;
}) {
    const { isDark } = useTheme();
    const [ref, inView] = useInView(0.06);
    const [btnHovered, setBtnHovered] = useState(false);
    const pct = Math.round((event.registered / event.capacity) * 100);
    const almostFull = pct >= 80;

    return (
        <div
            ref={ref as React.RefObject<HTMLDivElement>}
            style={{
                opacity: inView ? 1 : 0,
                transform: inView ? 'translateY(0)' : 'translateY(30px)',
                transition: 'all 0.4s cubic-bezier(.16,1,.3,1)',
                transitionDelay: `${index * 0.08}s`,
                display: 'flex',
                height: '100%',
                width: '100%',
            }}
        >
            <BorderGlow
                borderRadius={24}
                backgroundColor={isDark ? '#150d24' : '#ffffff'}
                style={{ width: '100%', height: '100%' }}
            >
                <div style={{
                    display: 'flex', flexDirection: 'column', height: '100%', width: '100%',
                    background: isDark ? '#150d24' : '#ffffff',
                    borderRadius: '24px', overflow: 'hidden',
                }}>
                    {/* Imagen de portada de evento con overlay cinemático */}
                    <div style={{ height: '165px', background: event.gradient, position: 'relative', overflow: 'hidden', flexShrink: 0 }}>
                        {event.image && (
                            <img
                                src={event.image}
                                alt={event.title}
                                style={{
                                    position: 'absolute', inset: 0, width: '100%', height: '100%',
                                    objectFit: 'cover',
                                    transition: 'transform 0.6s cubic-bezier(.16,1,.3,1)',
                                }}
                            />
                        )}
                        <div style={{
                            position: 'absolute', inset: 0,
                            background: event.image
                                ? 'linear-gradient(to top, rgba(21,13,36,0.95) 0%, rgba(21,13,36,0.45) 55%, rgba(21,13,36,0.2) 100%)'
                                : event.gradient,
                        }} />
                        <div style={{ position: 'absolute', top: '-40px', right: '-40px', width: '180px', height: '180px', background: 'rgba(255,255,255,0.07)', borderRadius: '50%', pointerEvents: 'none' }} />
                        <div style={{ position: 'absolute', bottom: '-30px', left: '-20px', width: '120px', height: '120px', background: 'rgba(255,255,255,0.04)', borderRadius: '50%', pointerEvents: 'none' }} />

                        {/* Tag badge */}
                        <div style={{ position: 'absolute', top: '14px', left: '14px', background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)', border: '1px solid rgba(255,255,255,0.2)', borderRadius: '8px', padding: '4px 12px', fontSize: '0.7rem', fontWeight: '700', color: '#fff', letterSpacing: '0.04em', zIndex: 3 }}>
                            {event.tag}
                        </div>
                        {/* Frequency */}
                        <div style={{ position: 'absolute', top: '14px', right: '14px', background: 'rgba(255,255,255,0.2)', backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)', border: '1px solid rgba(255,255,255,0.3)', borderRadius: '8px', padding: '4px 12px', fontSize: '0.7rem', fontWeight: '700', color: '#fff', zIndex: 3 }}>
                            {event.frequency}
                        </div>
                        {/* Big icon centered */}
                        <div style={{ position: 'absolute', bottom: '10px', left: '50%', transform: 'translateX(-50%)', fontSize: '2.6rem', filter: 'drop-shadow(0 6px 16px rgba(0,0,0,0.6))', zIndex: 3 }}>
                            {event.icon}
                        </div>
                    </div>

                    {/* Body */}
                    <div style={{ padding: '24px', display: 'flex', flexDirection: 'column', gap: '14px', flex: 1 }}>
                        {/* Date & time */}
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <span style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '0.78rem', fontWeight: '700', color: isDark ? '#FFD97C' : event.color }}>
                                <Calendar size={12} /> {event.date}
                            </span>
                            <span style={{ color: isDark ? '#475569' : '#e2e8f0' }}>·</span>
                            <span style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '0.78rem', fontWeight: '600', color: isDark ? '#cbd5e1' : '#64748b' }}>
                                <Clock size={12} /> {event.time}
                            </span>
                        </div>

                        {/* Title */}
                        <div>
                            <h3 style={{ fontSize: '1.15rem', fontWeight: '800', color: isDark ? '#ffffff' : '#0f172a', lineHeight: '1.2', letterSpacing: '-0.01em', marginBottom: '6px' }}>
                                {event.title}
                            </h3>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '0.75rem', color: isDark ? '#94a3b8' : '#94a3b8', fontWeight: '500' }}>
                                <MapPin size={12} /> {event.format}
                            </div>
                        </div>

                        {/* Description */}
                        <p style={{ fontSize: '0.85rem', color: isDark ? '#cbd5e1' : '#475569', lineHeight: '1.75', display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden', textAlign: 'justify' }}>
                            {event.description}
                        </p>

                        {/* Perks */}
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                            {event.perks.map(p => (
                                <span key={p} style={{
                                    background: isDark ? 'rgba(193,52,139,0.2)' : `${event.color}10`,
                                    border: isDark ? '1px solid rgba(193,52,139,0.35)' : `1px solid ${event.color}20`,
                                    borderRadius: '6px', padding: '3px 10px', fontSize: '0.7rem', fontWeight: '600',
                                    color: isDark ? '#FFD97C' : event.color,
                                }}>
                                    ✓ {p}
                                </span>
                            ))}
                        </div>

                        {/* Capacity bar */}
                        <div>
                            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '7px' }}>
                                <span style={{ fontSize: '0.74rem', fontWeight: '600', color: isDark ? '#cbd5e1' : '#64748b' }}>
                                    {event.registered} / {event.capacity} inscritos
                                </span>
                                <span style={{ fontSize: '0.74rem', fontWeight: '700', color: almostFull ? '#C1348B' : (isDark ? '#FFD97C' : '#94a3b8') }}>
                                    {almostFull ? '🔥 Casi lleno' : `${event.capacity - event.registered} cupos disponibles`}
                                </span>
                            </div>
                            {/* Barra de progreso */}
                            <div style={{ height: '7px', background: isDark ? '#23153c' : '#f1f5f9', borderRadius: '100px', overflow: 'hidden', padding: '1px' }}>
                                <div style={{
                                    height: '100%',
                                    width: `${Math.min(pct, 100)}%`,
                                    background: almostFull
                                        ? `linear-gradient(90deg, #61257B 0%, #C1348B 70%, #EA8406 100%)`
                                        : `linear-gradient(90deg, ${event.color}60 0%, ${event.color} 100%)`,
                                    borderRadius: '100px',
                                    boxShadow: `0 0 10px ${event.color}55`,
                                    transition: 'width 0.8s cubic-bezier(.16,1,.3,1)',
                                }} />
                            </div>
                        </div>

                        {/* CTA */}
                        <button
                            type="button"
                            onClick={() => { if (!event.isRegistered) onRegister(event); }}
                            onMouseEnter={() => { if (!event.isRegistered) setBtnHovered(true); }}
                            onMouseLeave={() => { if (!event.isRegistered) setBtnHovered(false); }}
                            style={{
                                width: '100%', padding: '12px 0', borderRadius: '12px', border: 'none',
                                background: event.isRegistered ? (isDark ? 'rgba(255,255,255,0.08)' : '#e2e8f0') : (btnHovered ? event.gradient : (isDark ? 'rgba(193,52,139,0.2)' : `${event.color}14`)),
                                color: event.isRegistered ? (isDark ? '#94a3b8' : '#64748b') : (btnHovered ? '#fff' : (isDark ? '#FFD97C' : event.color)),
                                fontWeight: '700', fontSize: '0.88rem',
                                fontFamily: "'DM Sans', sans-serif",
                                cursor: event.isRegistered ? 'not-allowed' : 'pointer',
                                boxShadow: event.isRegistered ? 'none' : (btnHovered ? `0 6px 20px rgba(97,37,123,0.35)` : 'none'),
                                transition: 'all 0.25s cubic-bezier(.16,1,.3,1)',
                                marginTop: 'auto',
                                display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '8px'
                            }}
                            disabled={event.isRegistered}
                        >
                            {event.isRegistered ? <><CheckCircle2 size={16} /> Ya estás inscrito</> : (event.registered >= event.capacity ? 'Lista de espera →' : 'Inscribirme ahora →')}
                        </button>
                    </div>
                </div>
            </BorderGlow>
        </div>
    );
}

// ── Stat card ──
function StatCard({ stat, index }: { stat: NetworkingStat; index: number }) {
    const { isDark } = useTheme();
    const [ref, inView] = useInView(0.1);

    return (
        <div
            ref={ref as React.RefObject<HTMLDivElement>}
            style={{
                opacity: inView ? 1 : 0,
                transform: inView ? 'translateY(0)' : 'translateY(24px)',
                transition: 'all 0.35s cubic-bezier(.16,1,.3,1)',
                transitionDelay: `${index * 0.08}s`,
            }}
        >
            <BorderGlow
                borderRadius={20}
                backgroundColor={isDark ? '#150d24' : '#ffffff'}
            >
                <div style={{
                    padding: '28px 24px', textAlign: 'center',
                    background: isDark ? '#150d24' : '#ffffff',
                    borderRadius: '20px',
                }}>
                    <div style={{ fontSize: '2rem', marginBottom: '10px' }}>{stat.icon}</div>
                    <div style={{ fontSize: '2.2rem', fontWeight: '900', color: isDark ? '#FFD97C' : stat.color, lineHeight: 1, letterSpacing: '-0.03em', marginBottom: '6px' }}>
                        {stat.value}
                    </div>
                    <div style={{ fontSize: '0.88rem', fontWeight: '700', color: isDark ? '#ffffff' : '#0f172a', marginBottom: '4px' }}>{stat.label}</div>
                    <div style={{ fontSize: '0.75rem', color: isDark ? '#94a3b8' : '#94a3b8' }}>{stat.sublabel}</div>
                </div>
            </BorderGlow>
        </div>
    );
}

// ── Testimonial Card ──
function TestimonialCard({ t, index }: { t: Testimonial; index: number }) {
    const { isDark } = useTheme();
    const [ref, inView] = useInView(0.08);

    return (
        <div
            ref={ref as React.RefObject<HTMLDivElement>}
            style={{
                transform: inView ? 'translateY(0)' : 'translateY(24px)',
                opacity: inView ? 1 : 0,
                transition: 'all 0.35s cubic-bezier(.16,1,.3,1)',
                transitionDelay: `${index * 0.08}s`,
                display: 'flex',
                height: '100%',
            }}
        >
            <BorderGlow
                borderRadius={22}
                backgroundColor={isDark ? '#150d24' : '#ffffff'}
                style={{ width: '100%', height: '100%' }}
            >
                <div style={{
                    padding: '28px 26px',
                    display: 'flex', flexDirection: 'column', gap: '18px',
                    height: '100%', background: isDark ? '#150d24' : '#ffffff',
                    borderRadius: '22px',
                }}>
                    {/* Quote mark */}
                    <div style={{ fontSize: '3rem', lineHeight: 1, color: `${t.color}35`, fontFamily: 'Georgia, serif', marginBottom: '-10px' }}>"</div>
                    <p style={{ fontSize: '0.9rem', color: isDark ? '#cbd5e1' : '#374151', lineHeight: '1.8', fontStyle: 'italic', textAlign: 'justify', flex: 1 }}>{t.quote}</p>
                    {/* Connection badge */}
                    <div style={{ display: 'inline-flex', alignSelf: 'flex-start', alignItems: 'center', gap: '6px', background: isDark ? 'rgba(193,52,139,0.2)' : `${t.color}12`, border: isDark ? '1px solid rgba(193,52,139,0.3)' : `1.5px solid ${t.color}25`, borderRadius: '100px', padding: '4px 12px' }}>
                        <span style={{ fontSize: '0.72rem', fontWeight: '700', color: isDark ? '#FFD97C' : t.color }}>✓ {t.connection}</span>
                    </div>
                    {/* Author */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', paddingTop: '8px', borderTop: isDark ? '1px solid rgba(193,52,139,0.2)' : '1.5px solid #f8fafc' }}>
                        <div style={{ width: '44px', height: '44px', borderRadius: '50%', background: `linear-gradient(135deg, ${t.color}, #C1348B)`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.88rem', fontWeight: '900', color: '#fff', flexShrink: 0, position: 'relative', overflow: 'hidden' }}>
                            <img src={t.avatar} alt={t.name} style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', borderRadius: '50%' }} onError={e => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }} />
                            {t.initials}
                        </div>
                        <div>
                            <div style={{ fontWeight: '800', fontSize: '0.88rem', color: isDark ? '#ffffff' : '#0f172a' }}>{t.name}</div>
                            <div style={{ fontSize: '0.75rem', color: isDark ? '#94a3b8' : '#94a3b8' }}>{t.role} · {t.company}</div>
                        </div>
                    </div>
                </div>
            </BorderGlow>
        </div>
    );
}

// ── How it works step ──
function StepCard({ step, index }: { step: HowItWorksStep; index: number }) {
    const { isDark } = useTheme();
    const [ref, inView] = useInView(0.1);

    return (
        <div
            ref={ref as React.RefObject<HTMLDivElement>}
            style={{
                transform: inView ? 'translateX(0)' : 'translateX(-30px)',
                opacity: inView ? 1 : 0,
                transition: 'all 0.4s cubic-bezier(.16,1,.3,1)',
                transitionDelay: `${index * 0.1}s`,
                display: 'flex',
                width: '100%',
            }}
        >
            <BorderGlow
                borderRadius={20}
                backgroundColor={isDark ? '#150d24' : '#ffffff'}
                style={{ width: '100%' }}
            >
                <div className="net-step-card" style={{
                    display: 'flex', gap: '20px', alignItems: 'flex-start',
                    padding: '24px 22px', width: '100%',
                    background: isDark ? '#150d24' : '#ffffff',
                    borderRadius: '20px',
                }}>
                    {/* Step number + icon */}
                    <div style={{ flexShrink: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '6px' }}>
                        <div style={{ width: '50px', height: '50px', borderRadius: '16px', background: isDark ? 'rgba(193,52,139,0.2)' : `${step.color}14`, border: isDark ? '1px solid rgba(193,52,139,0.3)' : `2px solid ${step.color}25`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.4rem' }}>
                            {step.icon}
                        </div>
                        <div style={{ fontSize: '0.65rem', fontWeight: '900', color: isDark ? '#FFD97C' : step.color, letterSpacing: '0.08em' }}>{step.step}</div>
                    </div>
                    <div>
                        <h4 style={{ fontSize: '1rem', fontWeight: '800', color: isDark ? '#ffffff' : '#0f172a', marginBottom: '6px' }}>{step.title}</h4>
                        <p style={{ fontSize: '0.84rem', color: isDark ? '#cbd5e1' : '#64748b', lineHeight: '1.7', textAlign: 'justify' }}>{step.description}</p>
                    </div>
                </div>
            </BorderGlow>
        </div>
    );
}

// ── Connection Viz ──
const nodes = [
    { x: 50, y: 50, label: 'Tú', color: '#61257B', size: 28 },
    { x: 22, y: 25, label: 'Founder', color: '#C1348B', size: 20 },
    { x: 78, y: 22, label: 'Inversor', color: '#EA8406', size: 22 },
    { x: 82, y: 72, label: 'CTO', color: '#61257B', size: 20 },
    { x: 20, y: 76, label: 'Mentor', color: '#C1348B', size: 20 },
    { x: 50, y: 15, label: 'Partner', color: '#EA8406', size: 18 },
    { x: 50, y: 86, label: 'Cliente', color: '#61257B', size: 18 },
];
const lines = [[0, 1], [0, 2], [0, 3], [0, 4], [0, 5], [0, 6], [1, 5], [2, 3], [3, 6], [4, 6]];

function ConnectionViz() {
    const { isDark } = useTheme();
    const [ref, inView] = useInView(0.15);

    return (
        <div
            ref={ref as React.RefObject<HTMLDivElement>}
            style={{
                opacity: inView ? 1 : 0,
                transform: inView ? 'translateX(0)' : 'translateX(30px)',
                transition: 'all 0.7s cubic-bezier(.16,1,.3,1)',
                display: 'flex',
                height: '100%',
                width: '100%',
            }}
        >
            <BorderGlow
                borderRadius={28}
                backgroundColor={isDark ? '#150d24' : '#ffffff'}
                style={{ width: '100%', height: '100%' }}
            >
                <div className="net-viz-card" style={{
                    padding: '36px', height: '100%', display: 'flex', flexDirection: 'column',
                    justifyContent: 'space-between', boxSizing: 'border-box',
                    background: isDark ? '#150d24' : '#ffffff', borderRadius: '28px',
                }}>
                    <div style={{ textAlign: 'center', marginBottom: '24px' }}>
                        <div style={{ fontSize: '0.72rem', fontWeight: '700', letterSpacing: '0.14em', textTransform: 'uppercase', color: isDark ? '#FFD97C' : '#61257B', marginBottom: '6px' }}>Tu red de contactos</div>
                        <h3 style={{ fontSize: '1.3rem', fontWeight: '800', color: isDark ? '#ffffff' : '#0f172a', letterSpacing: '-0.01em' }}>Un nodo en el ecosistema</h3>
                    </div>
                    <svg viewBox="0 0 100 100" style={{ width: '100%', maxWidth: '340px', display: 'block', margin: '0 auto', height: '240px' }}>
                        {/* Lines */}
                        {lines.map(([a, b], i) => (
                            <line key={i}
                                x1={`${nodes[a].x}%`} y1={`${nodes[a].y}%`}
                                x2={`${nodes[b].x}%`} y2={`${nodes[b].y}%`}
                                stroke={nodes[a].color} strokeWidth="0.5" strokeOpacity={isDark ? '0.5' : '0.35'}
                                strokeDasharray={inView ? '0' : '100'}
                                style={{ transition: `stroke-dasharray 1.5s ease ${i * 0.15}s`, strokeDashoffset: 0 }}
                            />
                        ))}
                        {/* Nodes */}
                        {nodes.map((n, i) => (
                            <g key={i}>
                                <circle cx={`${n.x}%`} cy={`${n.y}%`} r={`${n.size / 18}%`} fill={`${n.color}25`} stroke={n.color} strokeWidth="0.4" strokeOpacity="0.6" />
                                <circle cx={`${n.x}%`} cy={`${n.y}%`} r={`${n.size / 28}%`} fill={n.color} fillOpacity={inView ? '0.9' : '0'} style={{ transition: `fill-opacity 0.5s ease ${i * 0.1 + 0.3}s` }} />
                                <text x={`${n.x}%`} y={`${n.y + 6.5}%`} textAnchor="middle" fontSize="3.5" fill={isDark ? '#e2e8f0' : '#64748b'} fontFamily="DM Sans, sans-serif" fontWeight="600">
                                    {n.label}
                                </text>
                            </g>
                        ))}
                    </svg>
                    <p style={{ textAlign: 'justify', fontSize: '0.82rem', color: isDark ? '#cbd5e1' : '#94a3b8', lineHeight: '1.6', marginTop: '16px' }}>
                        Cada evento de Co Marca expande tu red con conexiones <strong style={{ color: isDark ? '#FFD97C' : '#475569' }}>con propósito y contexto compartido</strong>.
                    </p>
                </div>
            </BorderGlow>
        </div>
    );
}

// ── Hero ──
function HeroSection() {
    const { isDark } = useTheme();
    const [ref, inView] = useInView(0.1);
    const [forced, setForced] = useState(false);
    useEffect(() => { const t = setTimeout(() => setForced(true), 100); return () => clearTimeout(t); }, []);
    const visible = inView || forced;

    return (
        <section className="comunidad-net-hero" style={{
            background: isDark
                ? 'linear-gradient(135deg, #0b0614 0%, #150a26 50%, #200d36 100%)'
                : 'linear-gradient(135deg, #fff 0%, #faf5fc 55%, #f5e8fa 100%)',
            padding: 'clamp(100px, 14vw, 140px) 32px clamp(60px, 8vw, 80px)',
            position: 'relative', overflow: 'hidden',
        }}>
            <div className="blob-anim-net" style={{ position: 'absolute', top: '-80px', right: '-80px', width: '420px', height: '420px', background: isDark ? 'linear-gradient(135deg, rgba(193,52,139,0.18), rgba(97,37,123,0.12))' : 'linear-gradient(135deg, rgba(97,37,123,0.12), rgba(193,52,139,0.08))', pointerEvents: 'none' }} />
            {/* Malla hexagonal animada de izquierda a derecha con degradé suave */}
            <HexagonCornerDecoration
                variant="horizontal-degrade"
                direction="left-to-right"
                opacity={isDark ? 0.70 : 0.50}
                style={{
                    position: 'absolute',
                    inset: 0,
                    width: '100%',
                    height: '100%',
                    pointerEvents: 'none',
                    zIndex: 0,
                }}
            />
            <div ref={ref as React.RefObject<HTMLDivElement>} style={{ maxWidth: '1200px', margin: '0 auto', textAlign: 'center', position: 'relative' }}>
                <h1 className={`fade-up-net d1 ${visible ? 'in' : ''}`} style={{ fontSize: 'clamp(2.4rem, 5vw, 3.8rem)', fontWeight: '900', color: isDark ? '#ffffff' : '#0f172a', lineHeight: '1.08', letterSpacing: '-0.025em', marginBottom: '12px' }}>
                    Networking <span style={{ color: isDark ? '#FFD97C' : '#61257B' }}>que funciona</span>
                </h1>
                <h2 className={`fade-up-net d1 ${visible ? 'in' : ''}`} style={{ fontSize: 'clamp(1.1rem, 2vw, 1.4rem)', fontWeight: '600', color: isDark ? '#cbd5e1' : '#475569', letterSpacing: '-0.01em', marginBottom: '20px' }}>
                    Conexiones con <span style={{ color: isDark ? '#f472b6' : '#C1348B' }}>propósito, no con tarjetas</span>
                </h2>
                <p className={`fade-up-net d2 ${visible ? 'in' : ''}`} style={{ fontSize: '1.05rem', color: isDark ? '#94a3b8' : '#64748b', maxWidth: '580px', margin: '0 auto', lineHeight: '1.75', textAlign: 'justify' }}>
                    El networking genérico no funciona. En Co Marca diseñamos cada encuentro para que las conversaciones sean relevantes, los contactos sean reales y las alianzas que se forman aquí duren años.
                </p>
                <div className={`fade-up-net d3 ${visible ? 'in' : ''}`} style={{ display: 'flex', justifyContent: 'center', marginTop: '40px', flexWrap: 'wrap' }}>
                    {[
                        { value: '2,400+', label: 'Conexiones generadas', color: '#61257B' },
                        { value: '4 formatos', label: 'Distintos de eventos', color: '#C1348B' },
                        { value: 'Semanal', label: 'Frecuencia mínima', color: '#EA8406' },
                    ].map((s, i) => (
                        <div key={s.label} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '20px 36px', borderRight: i < 2 ? (isDark ? '1.5px solid rgba(193,52,139,0.25)' : '1.5px solid #ebdcf0') : 'none' }}>
                            <div style={{ fontSize: '2.6rem', fontWeight: '900', color: isDark ? '#FFD97C' : s.color, lineHeight: 1, letterSpacing: '-0.03em' }}>{s.value}</div>
                            <div style={{ fontSize: '0.82rem', color: isDark ? '#cbd5e1' : '#94a3b8', fontWeight: '500', marginTop: '6px' }}>{s.label}</div>
                        </div>
                    ))}
                </div>
                <div className={`fade-up-net d3 ${visible ? 'in' : ''}`} style={{ marginTop: '32px', display: 'flex', justifyContent: 'center' }}>
                    <BrandStripes
                        variant="scroll-prompt"
                        size="md"
                        promptText="Conecta con la comunidad"
                    />
                </div>
            </div>
        </section>
    );
}

// ── CTA ──
function CtaBanner() {
    const [ref, inView] = useInView(0.2);
    return (
        <div
            ref={ref as React.RefObject<HTMLDivElement>}
            style={{
                marginTop: '64px',
                opacity: inView ? 1 : 0,
                transform: inView ? 'translateY(0)' : 'translateY(30px)',
                transition: 'all 0.75s cubic-bezier(.16,1,.3,1)',
            }}
        >
            <BorderGlow
                borderRadius={28}
                backgroundColor="#420f54"
                style={{
                    background: 'linear-gradient(135deg, #420f54 0%, #61257B 50%, #C1348B 100%)',
                    boxShadow: '0 20px 48px rgba(97,37,123,0.28)',
                }}
            >
                <div style={{
                    padding: '56px 48px',
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '28px',
                    position: 'relative', overflow: 'hidden', width: '100%',
                }}>
                    <div style={{ position: 'absolute', top: '-60px', right: '-60px', width: '280px', height: '280px', background: 'rgba(255,255,255,0.08)', borderRadius: '50%' }} />
                    <div style={{ position: 'absolute', bottom: '-40px', left: '10%', width: '200px', height: '200px', background: 'rgba(255,255,255,0.05)', borderRadius: '50%' }} />
                    <div style={{ position: 'relative' }}>
                        <div style={{ fontSize: '0.75rem', fontWeight: '800', letterSpacing: '0.14em', textTransform: 'uppercase', color: '#FFD97C', marginBottom: '10px' }}>
                            ¿Listo para conectar?
                        </div>
                        <h3 style={{ fontSize: 'clamp(1.5rem, 3vw, 2.2rem)', fontWeight: '900', color: '#fff', lineHeight: '1.15', letterSpacing: '-0.02em' }}>
                            Tu próxima gran conexión<br />está en Co Marca
                        </h3>
                        <p style={{ fontSize: '1.0rem', color: 'rgba(255,255,255,0.9)', marginTop: '10px', maxWidth: '480px', lineHeight: '1.7', textAlign: 'justify' }}>
                            Inscríbete al próximo evento de networking, o visítanos cualquier miércoles sin reserva previa. Las mejores conexiones suelen ser las que no planificaste.
                        </p>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', flexShrink: 0 }}>
                        <a href="/servicios/eventos" style={{
                            display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '10px',
                            background: '#fff', color: '#61257B', borderRadius: '14px', padding: '14px 32px',
                            fontWeight: '800', fontSize: '0.97rem', textDecoration: 'none',
                            boxShadow: '0 8px 24px rgba(0,0,0,0.2)', transition: 'all 0.22s',
                        }}
                            onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 12px 28px rgba(0,0,0,0.25)'; }}
                            onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 8px 24px rgba(0,0,0,0.2)'; }}
                        >
                            Ver todos los eventos →
                        </a>
                        <a href="/contacto" style={{
                            display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '10px',
                            background: 'rgba(255,255,255,0.18)', color: '#fff', borderRadius: '14px', padding: '12px 32px',
                            fontWeight: '700', fontSize: '0.9rem', textDecoration: 'none',
                            border: '1.5px solid rgba(255,255,255,0.4)', transition: 'all 0.22s',
                            backdropFilter: 'blur(8px)',
                        }}
                            onMouseEnter={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.28)'; e.currentTarget.style.borderColor = '#FFD97C'; }}
                            onMouseLeave={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.18)'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.4)'; }}
                        >
                            Ser miembro activo →
                        </a>
                    </div>
                </div>
            </BorderGlow>
        </div>
    );
}

// ── Section label ──
function SectionLabel({ title, subtitle, color }: { title: string; subtitle: string; color: string }) {
    const { isDark } = useTheme();
    const [ref, inView] = useInView(0.1);
    return (
        <div ref={ref as React.RefObject<HTMLDivElement>} style={{
            marginBottom: '36px',
            opacity: inView ? 1 : 0, transform: inView ? 'translateY(0)' : 'translateY(18px)',
            transition: 'all 0.6s cubic-bezier(.16,1,.3,1)',
        }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
                <span style={{ fontSize: '0.75rem', fontWeight: '700', letterSpacing: '0.14em', textTransform: 'uppercase', color: isDark ? '#FFD97C' : color }}>{subtitle}</span>
                <div style={{ height: '2px', width: '48px', borderRadius: '2px', background: isDark ? '#FFD97C' : color }} />
            </div>
            <h2 style={{ fontSize: 'clamp(1.5rem, 2.5vw, 2rem)', fontWeight: '800', color: isDark ? '#ffffff' : '#0f172a', letterSpacing: '-0.02em' }}>
                {title}
            </h2>
        </div>
    );
}

// ── Networking RSVP Modal ──
function NetworkingRsvpModal({
    event,
    onClose,
    onSuccess,
}: {
    event: NetworkingEvent;
    onClose: () => void;
    onSuccess: (eventId: string) => void;
}) {
    const { isDark } = useTheme();
    const { user } = useAuth();

    // Auto-fill form if user is logged in
    const [name, setName] = useState(user ? `${user.first_name} ${user.last_name}`.trim() : '');
    const [email, setEmail] = useState(user?.email || '');
    const [phone, setPhone] = useState(user?.telefono || '');
    const [role, setRole] = useState(user?.especialidad || ''); // Map specialidad or nothing

    const [confirmed, setConfirmed] = useState(false);
    const [passCode, setPassCode] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [errorMsg, setErrorMsg] = useState('');

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setErrorMsg('');
        setSubmitting(true);
        try {
            if (event.rawId) {
                const res = await inscribirseEvento(event.rawId, {
                    nombre_completo: name,
                    email,
                    telefono: phone,
                    empresa_o_cargo: role,
                    cupos: 1,
                });
                setPassCode(res.codigo_pase);
                setConfirmed(true);
                onSuccess(event.id);
            } else {
                const code = `NET-${Math.floor(1000 + Math.random() * 9000)}`;
                setPassCode(code);
                setConfirmed(true);
                onSuccess(event.id);
            }
        } catch (err: any) {
            /* Browser diagnostics omitted. */
            const msg = err.response?.data?.non_field_errors?.[0] ||
                err.response?.data?.detail ||
                err.response?.data?.error ||
                'No se pudo completar el registro. Verifica los cupos e inténtalo de nuevo.';
            setErrorMsg(msg);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div
            style={{
                position: 'fixed', inset: 0, zIndex: 9999,
                background: 'rgba(0,0,0,0.72)', backdropFilter: 'blur(10px)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                padding: '20px',
            }}
            onClick={onClose}
        >
            <div
                onClick={e => e.stopPropagation()}
                style={{
                    background: isDark ? '#150d24' : '#ffffff',
                    borderRadius: '26px', maxWidth: '520px', width: '100%',
                    border: isDark ? '1.5px solid rgba(193,52,139,0.35)' : '1.5px solid #e2e8f0',
                    boxShadow: '0 25px 60px rgba(0,0,0,0.4)',
                    overflow: 'hidden', position: 'relative',
                    fontFamily: "'DM Sans', sans-serif",
                }}
            >
                <div style={{
                    background: event.gradient, padding: '26px 28px', color: '#fff', position: 'relative',
                }}>
                    <button
                        onClick={onClose}
                        style={{
                            position: 'absolute', top: '16px', right: '16px',
                            background: 'rgba(255,255,255,0.2)', border: 'none',
                            color: '#fff', width: '32px', height: '32px', borderRadius: '50%',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            cursor: 'pointer', fontSize: '1rem',
                        }}
                    >
                        ✕
                    </button>
                    <span style={{
                        background: 'rgba(255,255,255,0.18)', borderRadius: '100px',
                        padding: '3px 12px', fontSize: '0.7rem', fontWeight: '800',
                        letterSpacing: '0.06em', textTransform: 'uppercase',
                    }}>
                        Mesa de Networking Co Marca
                    </span>
                    <h3 style={{ fontSize: '1.35rem', fontWeight: '800', margin: '8px 0 4px' }}>
                        {event.title}
                    </h3>
                    <div style={{ fontSize: '0.82rem', opacity: 0.9 }}>
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}><Calendar size={14} /> {event.date}</span> · <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}><Clock size={14} /> {event.time}</span> · <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}><MapPin size={14} /> {event.format}</span>
                    </div>
                </div>

                <div style={{ padding: '26px 28px' }}>
                    {confirmed ? (
                        <div style={{ textAlign: 'center', padding: '10px 0' }}>
                            <div style={{ marginBottom: '8px' }}><Ticket size={48} /></div>
                            <h4 style={{ fontSize: '1.35rem', fontWeight: '800', color: isDark ? '#ffffff' : '#0f172a', marginBottom: '6px' }}>
                                ¡Pase de Networking Reservado!
                            </h4>
                            <p style={{ fontSize: '0.88rem', color: isDark ? '#cbd5e1' : '#64748b', marginBottom: '20px' }}>
                                Te hemos añadido a la lista de asistentes oficiales. Prepárate para conectar con founders e inversores activos.
                            </p>

                            <div style={{
                                background: isDark ? '#1b102e' : '#faf5fc',
                                border: isDark ? '1.5px dashed rgba(193,52,139,0.5)' : '1.5px dashed #d8b4fe',
                                borderRadius: '16px', padding: '16px', marginBottom: '20px', textAlign: 'left',
                            }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                                    <span style={{ fontSize: '0.7rem', fontWeight: '800', color: isDark ? '#FFD97C' : '#61257B' }}>
                                        ACCESO NETWORKING PASS
                                    </span>
                                    <span style={{ fontSize: '0.85rem', fontWeight: '900', color: '#C1348B', fontFamily: 'monospace' }}>
                                        {passCode}
                                    </span>
                                </div>
                                <div style={{ fontSize: '0.95rem', fontWeight: '700', color: isDark ? '#ffffff' : '#0f172a' }}>{name}</div>
                                <div style={{ fontSize: '0.8rem', color: isDark ? '#cbd5e1' : '#64748b' }}>{role} · {email}</div>
                            </div>

                            <button
                                onClick={onClose}
                                style={{
                                    width: '100%', padding: '12px', borderRadius: '12px',
                                    background: 'linear-gradient(135deg, #61257B, #C1348B)',
                                    color: '#fff', border: 'none', fontWeight: '700', cursor: 'pointer',
                                }}
                            >
                                Listo, nos vemos ahí
                            </button>
                        </div>
                    ) : (
                        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                            <div>
                                <label style={{ display: 'block', fontSize: '0.82rem', fontWeight: '600', color: isDark ? '#cbd5e1' : '#374151', marginBottom: '5px' }}>
                                    Nombre y Apellido *
                                </label>
                                <input
                                    type="text" required placeholder="Tu nombre"
                                    value={name} onChange={e => setName(e.target.value)}
                                    style={{
                                        width: '100%', padding: '10px 14px', borderRadius: '10px',
                                        border: isDark ? '1.5px solid rgba(193,52,139,0.35)' : '1.5px solid #e2e8f0',
                                        background: isDark ? '#190e2b' : '#fff', color: isDark ? '#fff' : '#0f172a',
                                        fontSize: '0.9rem', outline: 'none',
                                    }}
                                />
                            </div>

                            <div>
                                <label style={{ display: 'block', fontSize: '0.82rem', fontWeight: '600', color: isDark ? '#cbd5e1' : '#374151', marginBottom: '5px' }}>
                                    Correo electrónico *
                                </label>
                                <input
                                    type="email" required placeholder="tu@email.com"
                                    value={email} onChange={e => setEmail(e.target.value)}
                                    style={{
                                        width: '100%', padding: '10px 14px', borderRadius: '10px',
                                        border: isDark ? '1.5px solid rgba(193,52,139,0.35)' : '1.5px solid #e2e8f0',
                                        background: isDark ? '#190e2b' : '#fff', color: isDark ? '#fff' : '#0f172a',
                                        fontSize: '0.9rem', outline: 'none',
                                    }}
                                />
                            </div>

                            <div style={{ display: 'flex', gap: '12px' }}>
                                <div style={{ flex: 1 }}>
                                    <label style={{ display: 'block', fontSize: '0.82rem', fontWeight: '600', color: isDark ? '#cbd5e1' : '#374151', marginBottom: '5px' }}>
                                        WhatsApp *
                                    </label>
                                    <input
                                        type="tel" required placeholder="+591 7XX XXXXX"
                                        value={phone} onChange={e => setPhone(e.target.value)}
                                        style={{
                                            width: '100%', padding: '10px 14px', borderRadius: '10px',
                                            border: isDark ? '1.5px solid rgba(193,52,139,0.35)' : '1.5px solid #e2e8f0',
                                            background: isDark ? '#190e2b' : '#fff', color: isDark ? '#fff' : '#0f172a',
                                            fontSize: '0.9rem', outline: 'none',
                                        }}
                                    />
                                </div>
                                <div style={{ flex: 1 }}>
                                    <label style={{ display: 'block', fontSize: '0.82rem', fontWeight: '600', color: isDark ? '#cbd5e1' : '#374151', marginBottom: '5px' }}>
                                        Rol / Empresa *
                                    </label>
                                    <input
                                        type="text" required placeholder="Ej: Founder / Finanzas"
                                        value={role} onChange={e => setRole(e.target.value)}
                                        style={{
                                            width: '100%', padding: '10px 14px', borderRadius: '10px',
                                            border: isDark ? '1.5px solid rgba(193,52,139,0.35)' : '1.5px solid #e2e8f0',
                                            background: isDark ? '#190e2b' : '#fff', color: isDark ? '#fff' : '#0f172a',
                                            fontSize: '0.9rem', outline: 'none',
                                        }}
                                    />
                                </div>
                            </div>

                            {errorMsg && (
                                <div style={{
                                    padding: '10px 14px', borderRadius: '10px',
                                    background: 'rgba(239, 68, 68, 0.15)', border: '1px solid rgba(239, 68, 68, 0.4)',
                                    color: '#f87171', fontSize: '0.82rem', fontWeight: '600',
                                }}>
                                    ⚠️ {errorMsg}
                                </div>
                            )}

                            <button
                                type="submit"
                                disabled={submitting}
                                style={{
                                    marginTop: '8px', padding: '14px', borderRadius: '12px',
                                    border: 'none', background: submitting ? '#64748b' : event.gradient,
                                    color: '#fff', fontWeight: '800', fontSize: '0.95rem',
                                    cursor: submitting ? 'not-allowed' : 'pointer',
                                    boxShadow: '0 8px 24px rgba(97,37,123,0.35)',
                                    fontFamily: "'DM Sans', sans-serif",
                                }}
                            >
                                {submitting ? 'Confirmando reserva...' : 'Confirmar mi lugar en la mesa →'}
                            </button>
                        </form>
                    )}
                </div>
            </div>
        </div>
    );
}

// ── Main ──
const ComunidadNetworking: React.FC = () => {
    const { isDark } = useTheme();
    const [events, setEvents] = useState<NetworkingEvent[]>(initialEvents);
    const [activeFormat, setActiveFormat] = useState('todos');
    const [rsvpEvent, setRsvpEvent] = useState<NetworkingEvent | null>(null);

    const loadNetworkingEvents = useCallback(async () => {
        try {
            const apiEvents = await fetchEventosPublicos({ tipo: 'networking' });
            if (apiEvents && apiEvents.length > 0) {
                setEvents(apiEvents.map(mapApiEventoToNetworking));
            }
        } catch (err) {
            /* Browser diagnostics omitted. */
        }
    }, []);

    useEffect(() => {
        loadNetworkingEvents();
    }, [loadNetworkingEvents]);

    const handleSuccessRegistration = (eventId: string) => {
        setEvents(prev => prev.map(e => e.id === eventId ? { ...e, registered: Math.min(e.capacity, e.registered + 1) } : e));
        loadNetworkingEvents();
    };

    const formats = [
        { id: 'todos', label: 'Todos los formatos' },
        { id: 'presencial', label: 'Presencial' },
        { id: 'semanal', label: 'Semanal / Open House' },
        { id: 'exclusivo', label: 'Solo Invitación' },
        { id: 'hibrido', label: 'Híbrido' },
    ];

    const filteredEvents = events.filter(e => {
        if (activeFormat === 'todos') return true;
        if (activeFormat === 'presencial') return e.format.toLowerCase().includes('presencial');
        if (activeFormat === 'semanal') return e.frequency.toLowerCase().includes('semanal');
        if (activeFormat === 'exclusivo') return e.format.toLowerCase().includes('invitaci') || e.tag.toLowerCase().includes('exclusivo');
        if (activeFormat === 'hibrido') return e.format.toLowerCase().includes('streaming') || e.tag.toLowerCase().includes('híbrido');
        return true;
    });

    return (
        <div className="comunidad-net-page" style={{
            fontFamily: "'DM Sans', sans-serif",
            background: isDark ? '#0b0614' : '#f8fafc',
            minHeight: '100vh',
            transition: 'background-color 0.3s ease',
        }}>
            <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600;700;800;900&display=swap');
        * { box-sizing: border-box; margin: 0; padding: 0; }
        @keyframes blobFloat {
          0%, 100% { border-radius: 60% 40% 30% 70% / 60% 30% 70% 40%; }
          50% { border-radius: 30% 60% 70% 40% / 50% 60% 30% 60%; }
        }
        .blob-anim-net { animation: blobFloat 9s ease-in-out infinite; }
        .fade-up-net { opacity: 0; transform: translateY(28px); transition: opacity 0.75s ease, transform 0.75s cubic-bezier(.16,1,.3,1); }
        .fade-up-net.in { opacity: 1; transform: translateY(0); }
        .fade-up-net.d1 { transition-delay: 0.1s; }
        .fade-up-net.d2 { transition-delay: 0.2s; }
        .fade-up-net.d3 { transition-delay: 0.3s; }
        @media (max-width: 900px) {
          .net-process-grid { grid-template-columns: 1fr !important; }
        }
      `}</style>

            <HeroSection />

            <section style={{ padding: '60px 32px 100px', maxWidth: '1200px', margin: '0 auto' }}>

                {/* ── Stats ── */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: '16px', marginBottom: '72px' }}>
                    {stats.map((s, i) => <StatCard key={s.label} stat={s} index={i} />)}
                </div>

                {/* ── Próximos eventos ── */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '16px', marginBottom: '20px' }}>
                    <SectionLabel title="Próximos eventos de networking" subtitle="Agenda interactiva" color="#61257B" />

                    {/* Format Filter Pills */}
                    <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '28px' }}>
                        {formats.map(f => {
                            const active = activeFormat === f.id;
                            return (
                                <button
                                    key={f.id}
                                    onClick={() => setActiveFormat(f.id)}
                                    style={{
                                        padding: '7px 18px', borderRadius: '100px',
                                        background: active
                                            ? 'linear-gradient(135deg, #61257B, #C1348B)'
                                            : (isDark ? '#150d24' : '#fff'),
                                        color: active ? '#fff' : (isDark ? '#cbd5e1' : '#61257B'),
                                        border: active ? '1.5px solid transparent' : (isDark ? '1.5px solid rgba(193,52,139,0.3)' : '1.5px solid rgba(97,37,123,0.2)'),
                                        fontSize: '0.82rem', fontWeight: '700', cursor: 'pointer',
                                        boxShadow: active ? '0 4px 14px rgba(97,37,123,0.3)' : 'none',
                                        transition: 'all 0.2s',
                                    }}
                                >
                                    {f.label}
                                </button>
                            );
                        })}
                    </div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '20px', marginBottom: '72px' }}>
                    {filteredEvents.map((e, i) => (
                        <EventCard key={e.id} event={e} index={i} onRegister={setRsvpEvent} />
                    ))}
                </div>

                {/* ── Cómo funciona + visualización ── */}
                <SectionLabel title="Cómo funciona el networking en Co Marca" subtitle="El proceso" color="#C1348B" />
                <div className="net-process-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '28px', marginBottom: '72px', alignItems: 'start' }}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                        {steps.map((s, i) => <StepCard key={s.step} step={s} index={i} />)}
                    </div>
                    <ConnectionViz />
                </div>

                {/* ── Testimoniales ── */}
                <SectionLabel title="Lo que dicen quienes ya conectaron" subtitle="Testimonios" color={colors.accentDark} />
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '20px', marginBottom: '32px' }}>
                    {testimonials.map((t, i) => <TestimonialCard key={t.id} t={t} index={i} />)}
                </div>

                <CtaBanner />
            </section>

            {/* Modal de Registro Networking */}
            {rsvpEvent && (
                <NetworkingRsvpModal
                    event={rsvpEvent}
                    onClose={() => setRsvpEvent(null)}
                    onSuccess={handleSuccessRegistration}
                />
            )}
        </div>
    );
};

export default ComunidadNetworking;